import React from 'react';
import PropTypes from 'prop-types';
import { getHexTextColorHex } from 'library/colors';
import settings from 'settings';
import { timelineContextConfig } from 'config/menu.config';
import { isOta, signOnMenuState, TASK as SIGN_ON_TASK } from 'library/availableSignOn';
import { activityItemContent, leaveItemContent, travelItemContent } from 'library/helpers';
import * as rosterPivot from 'selectors/roster-pivot';
import Menu from './Menu';
import {
  actionDot, notesDot, notificationDot, signOnTriangle, validationIcon
} from './indicators';

const TIME_FORMAT = 'HH:mm - [Day] DDD';

export const assignmentTitleFormats = {
  title: i => i.title,
  start: i => i.start.format('HH:mm'),
  end: i => i.end.format('HH:mm')
};

/**
 * @param {object} item Assignment/leave/travel item
 * @param {string} textFormat The format of the title.
 * @returns {string} formatted title
 */
const getFormattedTitle = (item, textFormat = []) => {
  const title = [];
  if (!textFormat || !textFormat.length) {
    title.push(item.title);
  }
  const getFormat = (format, itemInstance) => (assignmentTitleFormats[format] ? assignmentTitleFormats[format](itemInstance) : format);

  return title.concat(textFormat.map(format => getFormat(format, item)))
    .join(' - ');
};

/**
 * @class renders an Activity or Travel Item.
 * @extends Component
 */
const ItemRenderer = ({
  onContextClick, item, timeFormat, titleFormat, className, pivotType, hiddenContextItems, showContextMenu, selectedItems
}) => {
  // formats
  const contextReadOnly = [];
  let popoverPrefix = '';
  let itemContent;
  let signOnOpts = {};

  const data = {
    ...item
  };

  if (Object.keys(item)
    .indexOf('booking') > 0) {
    itemContent = travelItemContent;
    data.color = settings.APP_CONFIG.defaultTravelActivityColor;
    popoverPrefix = `[ITINERARY] - ${data.user.last_name.toUpperCase()}, ${data.user.first_name} - `;
    contextReadOnly.push.apply(contextReadOnly, ['edit', 'reassign', 'copy']);
  }
  else if (Object.keys(item)
    .indexOf('leave') > 0) {
    itemContent = leaveItemContent;
    data.color = item.leave.type.color;
    popoverPrefix = '[LEAVE] ';
    contextReadOnly.push.apply(contextReadOnly, ['edit', 'reassign', 'travel', 'copy']);
  }
  else if (pivotType === rosterPivot.PIVOT_TYPES.USER) {
    // If the roster is grouped by users we want to show activity details.
    itemContent = activityItemContent;
    signOnOpts = { showSignOn: true };
    if (data.activity && data.activity.activity_type && data.activity.activity_type.code) {
      data.title = `${data.activity.name} (${data.activity.activity_type.code})`;
    }
    contextReadOnly.push.apply(contextReadOnly, ['travel']);
  }
  else if (pivotType === rosterPivot.PIVOT_TYPES.ACTIVITY) {
    // If the roster is grouped by activities we want to show user details.
    itemContent = activityItemContent;
    signOnOpts = { showSignOn: true };
    if (data.user) {
      if (data.user.first_name) {
        data.title = `${data.user.last_name}, ${data.user.first_name}`;
      }
      else {
        data.title = 'UNALLOCATED';
      }
    }
    contextReadOnly.push.apply(contextReadOnly, ['edit', 'reassign', 'travel', 'copy', SIGN_ON_TASK]);
  }
  // make unsaved assignments read only
  if (data.key < 0) {
    contextReadOnly.push.apply(contextReadOnly, ['action']);
  }
  // set item background and foregorund colours
  const style = {
    background: data.color,
    color: className.indexOf('rct9k-items-selected') === -1
      ? getHexTextColorHex(data.color)
      : 'black'
  };
  const signOn = signOnMenuState(data, selectedItems);
  const menuItems = showContextMenu
    ? timelineContextConfig(contextReadOnly, hiddenContextItems, signOn.visible ? [SIGN_ON_TASK] : [])
      .map(menuItem => (menuItem.task === SIGN_ON_TASK
        ? {
          ...menuItem,
          title: signOn.title,
          tooltip: signOn.tooltip,
          disabled: () => menuItem.disabled() || signOn.disabled
        }
        : menuItem))
    : [];
  return (
    <Menu
      data={item}
      menuItems={menuItems}
      onContextClick={onContextClick}
      title={`${popoverPrefix}${data.title}`}
      content={itemContent(data, timeFormat, signOnOpts)}
      className={className}
      style={style}
    >
      <span data-sign-on-anchor={isOta(data.activity) ? item.id : undefined}>
        {getFormattedTitle(data, titleFormat)}
        {notificationDot({ notifications: data.notifications })}
        {data.notes ? notesDot() : null}
        {data.admin_notes ? notesDot({ admin: true }) : null}
        {data.validations && data.validations.length ? validationIcon() : null}
        {data.latest_action ? actionDot({ resolved: !data.has_unresolved_actions }) : null}
        {isOta(data.activity) ? signOnTriangle({ set: !!data.available_sign_on }) : null}
      </span>
    </Menu>
  );
};

ItemRenderer.propTypes = {
  className: PropTypes.string,
  pivotType: PropTypes.string,
  item: PropTypes.object,
  timeFormat: PropTypes.string,
  titleFormat: PropTypes.array,
  showContextMenu: PropTypes.bool,
  hiddenContextItems: PropTypes.arrayOf(PropTypes.string),
  onContextClick: PropTypes.func,
  selectedItems: PropTypes.arrayOf(PropTypes.object)
};

ItemRenderer.defaultProps = {
  className: '',
  pivotType: rosterPivot.PIVOT_TYPES.USER,
  item: {},
  timeFormat: TIME_FORMAT,
  titleFormat: [],
  showContextMenu: true,
  hiddenContextItems: [],
  onContextClick() {
  },
  selectedItems: []
};

export default ItemRenderer;
